<template>
  <div class="invoice-document" :class="{ 'is-paid': selectedBill.status === 'paid', 'is-cancelled': selectedBill.is_deleted || selectedBill.status === 'cancelled' }">
    <!-- Watermark -->
    <div class="invoice-watermark" v-if="selectedBill.status === 'paid'">PAID</div>
    <div class="invoice-watermark cancelled" v-else-if="selectedBill.is_deleted || selectedBill.status === 'cancelled'">CANCELLED</div>

    <!-- Header -->
    <div class="invoice-doc-header">
      <div class="invoice-brand">
        <img
          v-if="selectedBill.house && selectedBill.house.house_logo"
          :src="$upload(selectedBill.house.house_logo)"
          alt="Logo"
          class="invoice-logo"
        />
        <div class="invoice-company-info">
          <h1 class="company-name">{{ selectedBill.house.house_name }}</h1>
          <p v-if="selectedBill.house.house_address">{{ selectedBill.house.house_address }}</p>
          <p v-if="selectedBill.house.house_phone">Tel: {{ selectedBill.house.house_phone }}</p>
          <p v-if="selectedBill.house.house_email">{{ selectedBill.house.house_email }}</p>
          <p v-if="selectedBill.house.house_tin_number">TIN: {{ selectedBill.house.house_tin_number }}</p>
        </div>
      </div>
      <div class="invoice-title-block">
        <h2 class="invoice-label">INVOICE</h2>
        <div class="invoice-number">#{{ selectedBill.bill_number }}</div>
      </div>
    </div>

    <div class="invoice-separator"></div>

    <!-- Meta Row -->
    <div class="invoice-meta-grid">
      <div class="invoice-to">
        <h4 class="meta-label">Bill To</h4>
        <p class="client-name" v-if="selectedBill.tenant">
          <template v-if="selectedBill.tenant.tenant_type === 'company'">
            {{ selectedBill.tenant.company_name || selectedBill.tenant.tenant_name }}
          </template>
          <template v-else>
            {{ selectedBill.tenant.tenant_name }}
          </template>
        </p>
        <p v-if="selectedBill.tenant && selectedBill.tenant.tenant_phone" class="client-detail">{{ selectedBill.tenant.tenant_phone }}</p>
        <p v-if="selectedBill.tenant && selectedBill.tenant.tenant_email" class="client-detail">{{ selectedBill.tenant.tenant_email }}</p>
        <p v-if="selectedBill.tenant && selectedBill.tenant.tin_number" class="client-detail">TIN: {{ selectedBill.tenant.tin_number }}</p>
      </div>
      <div class="invoice-details">
        <table class="details-table">
          <tr>
            <td class="dt-label">Invoice Date</td>
            <td class="dt-value">{{ $date(selectedBill.created_at) }}</td>
          </tr>
          <tr>
            <td class="dt-label">Due Date</td>
            <td class="dt-value">{{ $date(selectedBill.due_date) }}</td>
          </tr>
          <tr v-if="selectedBill.room">
            <td class="dt-label">Room</td>
            <td class="dt-value">{{ selectedBill.room.room_number }}</td>
          </tr>
          <tr>
            <td class="dt-label">Status</td>
            <td class="dt-value">
              <span :class="['invoice-status-badge', selectedBill.status]">
                {{ selectedBill.is_deleted || selectedBill.status === 'cancelled' ? 'Cancelled' : selectedBill.status === 'paid' ? 'Paid' : 'Pending' }}
              </span>
            </td>
          </tr>
        </table>
      </div>
    </div>

    <!-- Description -->
    <div class="invoice-description" v-if="selectedBill.bill_description">
      <span class="desc-label">Description:</span> {{ selectedBill.bill_description }}
    </div>

    <!-- Line Items Table -->
    <div class="invoice-items-section">
      <table class="invoice-items-table">
        <thead>
          <tr>
            <th class="col-num">#</th>
            <th class="col-desc">Description</th>
            <th class="col-price">Unit Price</th>
            <th class="col-qty">Qty</th>
            <th class="col-total">Amount</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="selectedBill.bill_details && selectedBill.bill_details.length > 0">
            <tr v-for="(item, index) in selectedBill.bill_details" :key="item.id">
              <td class="col-num">{{ index + 1 }}</td>
              <td class="col-desc">
                <div v-if="editingId === item.id" class="label-edit no-print">
                  <input
                    v-model="editLabel"
                    type="text"
                    maxlength="255"
                    class="label-edit-input"
                    :disabled="savingLabel"
                    @keyup.enter="saveLabel(item)"
                    @keyup.esc="cancelEdit"
                  />
                  <button
                    type="button"
                    class="label-btn save"
                    :disabled="savingLabel"
                    title="Save label"
                    @click="saveLabel(item)"
                  >
                    <i class="fa fa-check"></i>
                  </button>
                  <button
                    type="button"
                    class="label-btn cancel"
                    :disabled="savingLabel"
                    title="Cancel"
                    @click="cancelEdit"
                  >
                    <i class="fa fa-xmark"></i>
                  </button>
                </div>
                <template v-else>
                  <span class="label-text">{{ item.bill_name }}</span>
                  <button
                    v-if="canEdit && item.id"
                    type="button"
                    class="label-edit-trigger no-print"
                    title="Rename label"
                    @click="startEdit(item)"
                  >
                    <i class="fa fa-pen"></i> Rename
                  </button>
                </template>
              </td>
              <td class="col-price">{{ $comma(item.bill_price) }}</td>
              <td class="col-qty">{{ item.bill_quantity }}</td>
              <td class="col-total">{{ $comma(item.bill_price * item.bill_quantity) }}</td>
            </tr>
          </template>
          <tr v-else>
            <td class="col-num">1</td>
            <td class="col-desc">{{ selectedBill.bill_description || 'Invoice charge' }}</td>
            <td class="col-price">{{ $comma(selectedBill.amount / (selectedBill.quantity || 1)) }}</td>
            <td class="col-qty">{{ selectedBill.quantity || 1 }}</td>
            <td class="col-total">{{ $comma(selectedBill.amount) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Totals -->
    <div class="invoice-totals-section">
      <div class="invoice-totals">
        <div class="totals-row" v-if="showVat">
          <span class="totals-label">Subtotal</span>
          <span class="totals-value">{{ $comma(selectedBill.amount) }} {{ currency }}</span>
        </div>
        <div class="totals-row" v-if="showVat">
          <span class="totals-label">VAT ({{ selectedBill.house?.vat_percentage || $getHouse()?.vat_percentage || 0 }}%)</span>
          <span class="totals-value">{{ $comma(selectedBill.vat_amount) }} {{ currency }}</span>
        </div>
        <div class="totals-row total-final">
          <span class="totals-label">Total Due</span>
          <span class="totals-value">{{ $comma(totalAmount) }} {{ currency }}</span>
        </div>
        <div class="totals-row paid-row" v-if="selectedBill.paid_amount > 0">
          <span class="totals-label">Amount Paid</span>
          <span class="totals-value paid">{{ $comma(selectedBill.paid_amount) }} {{ currency }}</span>
        </div>
        <div class="totals-row balance-row" v-if="selectedBill.paid_amount > 0 && balance > 0">
          <span class="totals-label">Balance Due</span>
          <span class="totals-value balance">{{ $comma(balance) }} {{ currency }}</span>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="invoice-doc-footer">
      <div class="invoice-note" v-if="selectedBill.message">
        <h4>Note</h4>
        <p>{{ selectedBill.message }}</p>
      </div>
      <div class="invoice-footer-line">
        <p>Thank you for your prompt payment.</p>
        <p class="generated-at">Generated on {{ currentDateTime }}</p>
        <p class="powered-by">Powered by <strong>Ireme Technologies</strong></p>
      </div>
    </div>

    <!-- Print Button -->
    <div class="invoice-print-actions">
      <button class="print-btn-main" @click="printBill">
        <i class="fa fa-print"></i> Print Invoice
      </button>
    </div>
  </div>
</template>

<script>
import { toast } from "vue3-toastify";

export default {
  name: "ViewBill",
  props: {
    selectedBill: { type: Object, required: true },
    // When true (and the admin has Invoices/edit), each line label gets an inline
    // rename control. Off by default so tenant-facing invoices stay read-only.
    editable: { type: Boolean, default: false },
  },
  emits: ["labelUpdated"],
  data() {
    return {
      editingId: null,
      editLabel: "",
      savingLabel: false,
    };
  },
  computed: {
    canEdit() {
      return (
        this.editable &&
        typeof this.$access === "function" &&
        this.$access("Invoices", "edit")
      );
    },
    currency() {
      return this.$getHouse()?.currency || '';
    },
    // VAT is shown only when the bill has a VAT amount AND the house hasn't turned the
    // "Show VAT on invoices" toggle off. Reads the flag from the bill's own house so it
    // works in both admin and tenant contexts.
    showVat() {
      const houseAllows = this.selectedBill?.house?.show_vat_on_invoice !== false;
      return houseAllows && (parseFloat(this.selectedBill?.vat_amount) || 0) > 0;
    },
    totalAmount() {
      return parseFloat(this.selectedBill.amount) || 0;
    },
    balance() {
      return this.totalAmount - (parseFloat(this.selectedBill.paid_amount) || 0);
    },
    currentDateTime() {
      const now = new Date();
      return this.$date(now) + ' ' +
        String(now.getHours()).padStart(2, '0') + ':' +
        String(now.getMinutes()).padStart(2, '0');
    },
  },
  methods: {
    startEdit(item) {
      this.editingId = item.id;
      this.editLabel = item.bill_name || "";
    },
    cancelEdit() {
      this.editingId = null;
      this.editLabel = "";
    },
    async saveLabel(item) {
      const label = (this.editLabel || "").trim();
      if (!label) {
        toast.error("Label cannot be empty.");
        return;
      }
      if (label === (item.bill_name || "")) {
        this.cancelEdit();
        return;
      }
      const token = this.$getAdminToken?.();
      const houseId = this.$getHouseId?.();
      if (!token || !houseId) {
        toast.error("Session missing house or token.");
        return;
      }
      this.savingLabel = true;
      try {
        const res = await this.$store.dispatch("UPDATE_LINE_LABEL", {
          token,
          data: {
            house_id: houseId,
            bill_id: this.selectedBill.bill_id,
            detail_id: item.id,
            bill_name: label,
          },
        });
        if (!res || res.status !== 200 || res.data?.status !== "success") {
          throw new Error(res?.data?.message || "Failed to update label");
        }
        // Reflect the change immediately, then let the parent reload if it wants to.
        item.bill_name = res.data?.data?.bill_name || label;
        toast.success("Label updated.");
        this.$emit("labelUpdated", {
          detail_id: item.id,
          bill_name: item.bill_name,
        });
        this.cancelEdit();
      } catch (e) {
        toast.error(e?.message || "Failed to update label.");
      } finally {
        this.savingLabel = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/main.scss";

.invoice-document {
  position: relative;
  background: #fff;
  max-width: 800px;
  margin: 0 auto;
  padding: 2.5rem;
  font-family: "Poppins", sans-serif;
  color: #1a1a2e;
  overflow: hidden;

  p {
    margin-bottom: 0 !important;
  }
}

/* Watermark */
.invoice-watermark {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-35deg);
  font-size: 7rem;
  font-weight: 800;
  color: rgba(40, 167, 69, 0.07);
  letter-spacing: 0.15em;
  pointer-events: none;
  z-index: 0;
  user-select: none;
  white-space: nowrap;

  &.cancelled {
    color: rgba(220, 53, 69, 0.07);
  }
}

/* Header */
.invoice-doc-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
  position: relative;
  z-index: 1;
}

.invoice-brand {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1;
}

.invoice-logo {
  width: 60px;
  height: 60px;
  object-fit: contain;
  border-radius: 8px;
  flex-shrink: 0;
}

.invoice-company-info {
  h1.company-name {
    font-size: 1.2rem;
    font-weight: 700;
    color: $secondary_color;
    margin: 0 0 0.25rem 0;
    line-height: 1.2;
  }
  p {
    font-size: 0.8rem;
    color: #5a6577;
    line-height: 1.45;
  }
}

.invoice-title-block {
  text-align: right;
  flex-shrink: 0;

  .invoice-label {
    font-size: 2rem;
    font-weight: 800;
    color: $primary_color;
    margin: 0;
    line-height: 1;
    letter-spacing: 0.05em;
  }
  .invoice-number {
    font-size: 0.95rem;
    color: #5a6577;
    margin-top: 0.25rem;
    font-weight: 500;
  }
}

/* Separator */
.invoice-separator {
  height: 3px;
  background: linear-gradient(90deg, $primary_color, lighten($primary_color, 25%));
  border-radius: 2px;
  margin: 1.5rem 0;
}

/* Meta Grid */
.invoice-meta-grid {
  display: flex;
  justify-content: space-between;
  gap: 2rem;
  margin-bottom: 1.75rem;
  position: relative;
  z-index: 1;
}

.invoice-to {
  flex: 1;

  .meta-label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #94a3b8;
    margin: 0 0 0.4rem 0;
  }
  .client-name {
    font-size: 1rem;
    font-weight: 600;
    color: $secondary_color;
  }
  .client-detail {
    font-size: 0.8rem;
    color: #5a6577;
    line-height: 1.5;
  }
}

.invoice-details {
  flex-shrink: 0;
}

.details-table {
  border-collapse: collapse;

  .dt-label {
    font-size: 0.78rem;
    color: #94a3b8;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 0.2rem 1rem 0.2rem 0;
    white-space: nowrap;
  }
  .dt-value {
    font-size: 0.85rem;
    color: $secondary_color;
    font-weight: 500;
    padding: 0.2rem 0;
    text-align: right;
  }
}

.invoice-status-badge {
  display: inline-block;
  padding: 0.15rem 0.6rem;
  border-radius: 50px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;

  &.paid {
    background: #d4edda;
    color: #155724;
  }
  &.pending {
    background: #fff3cd;
    color: #856404;
  }
  &.cancelled {
    background: #e2e3e5;
    color: #383d41;
  }
}

/* Description */
.invoice-description {
  background: #f8f9fb;
  border-left: 3px solid $primary_color;
  padding: 0.65rem 1rem;
  border-radius: 0 6px 6px 0;
  margin-bottom: 1.5rem;
  font-size: 0.85rem;
  color: #4a5568;
  position: relative;
  z-index: 1;

  .desc-label {
    font-weight: 600;
    color: #2d3748;
  }
}

/* Items Table */
.invoice-items-section {
  position: relative;
  z-index: 1;
  margin-bottom: 0;
}

.invoice-items-table {
  width: 100%;
  border-collapse: collapse;

  thead tr {
    background: $secondary_color;

    th {
      padding: 0.65rem 0.75rem;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #fff;
      text-align: left;

      &.col-num {
        width: 40px;
        text-align: center;
      }
      &.col-price,
      &.col-qty,
      &.col-total {
        text-align: right;
      }
    }
  }

  tbody tr {
    border-bottom: 1px solid #eef0f3;
    transition: background 0.15s;

    &:hover {
      background: #f8f9fb;

      .label-edit-trigger {
        opacity: 1;
      }
    }

    &:last-child {
      border-bottom: 2px solid $secondary_color;
    }

    td {
      padding: 0.7rem 0.75rem;
      font-size: 0.85rem;
      color: #2d3748;

      &.col-num {
        text-align: center;
        color: #94a3b8;
        font-weight: 500;
      }
      &.col-desc {
        font-weight: 500;

        .label-text {
          vertical-align: middle;
        }
        .label-edit-trigger {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          margin-left: 8px;
          padding: 2px 7px;
          border: 1px solid $primary_color;
          background: #eef2ff;
          border-radius: 5px;
          color: $primary_color;
          font-size: 0.68rem;
          cursor: pointer;
          opacity: 0.85;
          transition: opacity 0.15s, background 0.15s;
          vertical-align: middle;
          &:hover {
            opacity: 1;
            background: #e0e7ff;
          }
        }
        .label-edit {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .label-edit-input {
          padding: 4px 8px;
          border: 1px solid $primary_color;
          border-radius: 5px;
          font-size: 0.82rem;
          min-width: 180px;
          outline: none;
        }
        .label-btn {
          padding: 4px 8px;
          border: 1px solid #e2e8f0;
          background: #fff;
          border-radius: 5px;
          cursor: pointer;
          font-size: 0.72rem;
          &.save {
            color: #047857;
            border-color: #a7f3d0;
          }
          &.cancel {
            color: #b91c1c;
            border-color: #fecaca;
          }
          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        }
      }
      &.col-price,
      &.col-qty,
      &.col-total {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }
      &.col-total {
        font-weight: 600;
      }
    }
  }
}

/* Totals */
.invoice-totals-section {
  display: flex;
  justify-content: flex-end;
  position: relative;
  z-index: 1;
}

.invoice-totals {
  width: 280px;
  margin-top: 0;

  .totals-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.45rem 0.75rem;
    font-size: 0.85rem;

    .totals-label {
      color: #5a6577;
      font-weight: 500;
    }
    .totals-value {
      font-weight: 600;
      color: #2d3748;
      font-variant-numeric: tabular-nums;
    }
  }

  .total-final {
    background: $primary_color;
    border-radius: 6px;
    margin-top: 0.25rem;

    .totals-label {
      color: #fff;
      font-weight: 700;
      font-size: 0.9rem;
    }
    .totals-value {
      color: #fff;
      font-weight: 700;
      font-size: 1.05rem;
    }
  }

  .paid-row {
    margin-top: 0.35rem;
    .totals-value.paid {
      color: #28a745;
    }
  }

  .balance-row {
    border-top: 1px dashed #d1d5db;
    padding-top: 0.5rem;
    .totals-label {
      font-weight: 700;
      color: #2d3748;
    }
    .totals-value.balance {
      font-weight: 700;
      color: #dc3545;
      font-size: 1rem;
    }
  }
}

/* Footer */
.invoice-doc-footer {
  margin-top: 2rem;
  padding-top: 1.25rem;
  border-top: 1px solid #eef0f3;
  position: relative;
  z-index: 1;

  .invoice-note {
    background: #f8f9fb;
    padding: 0.75rem 1rem;
    border-radius: 6px;
    margin-bottom: 1rem;

    h4 {
      font-size: 0.75rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #94a3b8;
      margin: 0 0 0.25rem 0;
    }
    p {
      font-size: 0.82rem;
      color: #4a5568;
      line-height: 1.5;
      white-space: pre-wrap;
    }
  }

  .invoice-footer-line {
    text-align: center;

    p {
      font-size: 0.8rem;
      color: #94a3b8;
      line-height: 1.5;
    }
    .generated-at {
      font-size: 0.7rem;
      color: #c0c7d1;
      margin-top: 0.15rem;
    }
    .powered-by {
      font-size: 0.7rem;
      color: #9ca3af;
      margin-top: 0.35rem;
      strong {
        color: $primary_color;
      }
    }
  }
}

/* Print Actions - hidden in print */
.invoice-print-actions {
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
  position: relative;
  z-index: 1;

  .print-btn-main {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.6rem 1.75rem;
    background: $primary_color;
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s, transform 0.1s;

    &:hover {
      background: $primary_color_hover;
      transform: translateY(-1px);
    }
    &:active {
      transform: translateY(0);
    }

    i {
      font-size: 1rem;
    }
  }
}

/* Responsive */
@media (max-width: 600px) {
  .invoice-document {
    padding: 1.25rem;
  }

  .invoice-doc-header {
    flex-direction: column;
    gap: 1rem;
  }

  .invoice-title-block {
    text-align: left;
    .invoice-label {
      font-size: 1.5rem;
    }
  }

  .invoice-meta-grid {
    flex-direction: column;
    gap: 1rem;
  }

  .invoice-totals {
    width: 100%;
  }

  .invoice-items-table {
    font-size: 0.78rem;

    thead th,
    tbody td {
      padding: 0.5rem;
    }
  }
}

/* Print styles */
@media print {
  .invoice-print-actions,
  .no-print {
    display: none !important;
  }

  .invoice-document {
    box-shadow: none;
    padding: 0;
    margin: 0;
  }

  .invoice-watermark {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
